(() => {
  if (customElements.get('arctic-rail')) return;

  class ArcticRail extends HTMLElement {
    connectedCallback() {
      if (this.embla) return;
      // Native horizontal scrolling remains usable if the optional engine fails.
      if (!window.EmblaCarousel) return;

      const viewport = this.querySelector('.arctic-rail__viewport');
      if (!viewport) return;

      const slides = viewport.querySelectorAll('.arctic-rail__slide');
      if (slides.length === 0) {
        // Slides may arrive after mount when the rail is nested inside a
        // lazy-fetch wrapper (e.g. Dawn's <product-recommendations>). Watch
        // the viewport and re-run init once slides are populated.
        this.waitForSlides(viewport);
        return;
      }

      this.initEmbla(viewport);
    }

    waitForSlides(viewport) {
      if (this.slideObserver) return;

      this.slideObserver = new MutationObserver(() => {
        if (this.embla) return;
        if (viewport.querySelector('.arctic-rail__slide')) {
          this.slideObserver.disconnect();
          this.slideObserver = null;
          this.initEmbla(viewport);
        }
      });

      this.slideObserver.observe(viewport, { childList: true, subtree: true });
    }

    initEmbla(viewport) {
      if (this.embla) return;
      const options = {
        align: this.dataset.align || 'start',
        containScroll: 'trimSnaps',
        dragFree: this.dataset.dragFree !== 'false',
        slidesToScroll: 'auto',
        duration: 24,
        breakpoints: { '(prefers-reduced-motion: reduce)': { duration: 0 } },
      };

      this.embla = window.EmblaCarousel(viewport, options);
      this.setAttribute('data-rail-ready', '');

      const prev = this.querySelector('[data-rail-prev]');
      const next = this.querySelector('[data-rail-next]');
      const progress = this.querySelector('[data-rail-progress]');
      const controls = [prev, next].filter(Boolean);

      controls.forEach((control) => {
        if (viewport.id && !control.hasAttribute('aria-controls')) {
          control.setAttribute('aria-controls', viewport.id);
        }
      });

      const updateButtons = () => {
        if (prev) this.setControlDisabled(prev, !this.embla.canScrollPrev());
        if (next) this.setControlDisabled(next, !this.embla.canScrollNext());
      };

      const updateProgress = () => {
        if (!progress) return;

        const canScroll = this.embla.canScrollPrev() || this.embla.canScrollNext();
        const snapCount = this.embla.scrollSnapList().length || 1;
        const thumbSize = canScroll ? Math.max(100 / snapCount, 16) : 100;
        const scrollProgress = Math.min(Math.max(this.embla.scrollProgress(), 0), 1);
        const thumbLeft = canScroll ? scrollProgress * (100 - thumbSize) : 0;

        progress.style.setProperty('--rail-progress-size', `${thumbSize}%`);
        progress.style.setProperty('--rail-progress-left', `${thumbLeft}%`);
      };

      this.refresh = () => {
        if (!this.embla) return;
        this.embla.reInit();
        updateButtons();
        updateProgress();
      };

      this.controlListeners = [];
      for (const [control, method] of [[prev, 'scrollPrev'], [next, 'scrollNext']]) {
        if (!control) continue;
        const listener = () => this.embla?.[method]();
        control.addEventListener('click', listener);
        this.controlListeners.push([control, listener]);
      }

      this.embla.on('select', updateButtons);
      this.embla.on('select', updateProgress);
      this.embla.on('scroll', updateProgress);
      this.embla.on('reInit', updateButtons);
      this.embla.on('reInit', updateProgress);
      updateButtons();
      updateProgress();
    }

    setControlDisabled(control, disabled) {
      control.disabled = disabled;
      control.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    disconnectedCallback() {
      this.removeAttribute('data-rail-ready');
      for (const [control, listener] of this.controlListeners || []) {
        control.removeEventListener('click', listener);
        this.setControlDisabled(control, true);
      }
      this.controlListeners = [];
      this.refresh = null;
      if (this.slideObserver) {
        this.slideObserver.disconnect();
        this.slideObserver = null;
      }
      if (this.embla) {
        this.embla.destroy();
        this.embla = null;
      }
    }
  }

  customElements.define('arctic-rail', ArcticRail);
})();
